import styled from "styled-components";
import React, {useContext, useEffect, useState} from "react";
import Image from "next/legacy/image";

import Indicator from "./Indicator";
import * as vars from "../../shared/variables";
import Icon from "../Icon";
import Button from "../Button";
import MiniCartProduct from "../product/MiniCartProduct";
import {useShoppingCart} from "../../hooks/useShoppingCart";
import {ICartItem} from "../../interfaces/ICartItem";
import {useRouter} from "next/router";
import {useMessages} from "../../hooks/useMessages";
import {useUserLoggedIn} from "../../hooks/useUserLoggedIn";
import {getLocalStorageItem, setLocalStorageItem} from "../../shared/localStorageUtil";
import {ILoggedOutShoppingList} from "../../interfaces/ILoggedOutShoppingList";
import {LoggedOutCartContext} from "../../contexts/LoggedOutCartContext";
import {useCountryLang} from "../../hooks/useCountryLang";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {ITotalForProducts} from "../../interfaces/ITotalForProducts";
import {fetchLoggedOutCartTotal} from "../../pages/[country]/[lang]/cart";
import {getTheme} from "../../shared/themeUtils";
import {getNavPath} from "../../shared/navigateUtil";
import {useShippingCosts} from "../../hooks/useShippingCosts";

const CartOffCanvas = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px;
`

const Main = styled.div`
  height: 100%;
  overflow-y: auto;
`

const Bottom = styled.div`
  color: ${getTheme("colors.primary")};
  border-top: 2px solid ${getTheme("colors.primary")};
  padding-top: ${getTheme("space.1")};
  
  .cart-total {
    font-weight: bold;
    text-align: center;
     font-size: ${getTheme("fontSizes.large")}
  }

  button {
    margin-top: 10px;
  }

    .shipping-costs {
        text-align: center;
        font-size: ${getTheme("fontSizes.small")};
        font-weight: 500;
    }
`

const Header = styled.div`
  border-bottom: 1px solid ${getTheme("colors.border")};
  padding: 0 10px 20px 0;
  color: ${getTheme("colors.primary")};
  font-weight: 500;
  display: flex;
  align-items: center;

  span {
    margin-left: 10px;
  }
`

const EmptyCart = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${getTheme("colors.secondaryFont")};
  margin-bottom: 60px;
`

const IndicatorMiniCart = () => {
  const isUserLoggedIn = useUserLoggedIn();
  const [hasItems, setHasItems] = useState(false);
  const shoppingCartQuery = useShoppingCart();
  const router = useRouter();
  const messagesQuery = useMessages()
  const {loggedOutCartCtx} = useContext(LoggedOutCartContext)
  const [cartItemsCount, setCartItemsCount] = useState(0)
  const countryLang = useCountryLang()
  const shippingCostsQuery = useShippingCosts()
  const queryClient = useQueryClient();

  // Clean up orphaned setup options
  useEffect(() => {
    if (!isUserLoggedIn) {
      const loggedOutSetupOptions = getLocalStorageItem("loggedOutSetupOptions");
      const loggedOutCart = getLocalStorageItem("loggedOutCart");
      
      if (loggedOutSetupOptions && loggedOutCart) {
        const setupEntries = [...JSON.parse(loggedOutSetupOptions)];
        const cartEntries = [...JSON.parse(loggedOutCart)];
        
        // Filter out setup options that don't have corresponding products in cart
        const validEntries = setupEntries.filter(entry => {
          return cartEntries.some(cartItem => 
            cartItem.product.productID === entry.productId
          );
        });
        
        // Only update localStorage if we found orphaned entries
        if (validEntries.length !== setupEntries.length) {
          setLocalStorageItem("loggedOutSetupOptions", JSON.stringify(validEntries));
          queryClient.invalidateQueries(["loggedOutCartTotal"]);
        }
      } else if (loggedOutSetupOptions && !loggedOutCart) {
        // If there's no cart but there are setup options, clear all setup options
        setLocalStorageItem("loggedOutSetupOptions", JSON.stringify([]));
        queryClient.invalidateQueries(["loggedOutCartTotal"]);
      }
    }
  }, [isUserLoggedIn, loggedOutCartCtx, shoppingCartQuery.data]);

  useEffect(() => {
    // initialize on each cycle with reset values
    setHasItems(false)
    setCartItemsCount(0)
    // logged-out user
    const persistedLogoutCart = getLocalStorageItem("loggedOutCart");
    if (persistedLogoutCart != null) {
      setCartItemsCount(JSON.parse(persistedLogoutCart).length)
    }

    // logged-in user
    if (shoppingCartQuery.data && shoppingCartQuery.data.products && shoppingCartQuery.data.products.length > 0) {
      setHasItems(true)
      setCartItemsCount(shoppingCartQuery.data.products.length)
    }
    if (loggedOutCartCtx != null && loggedOutCartCtx.length > 0) {
      setHasItems(true)
      setCartItemsCount(loggedOutCartCtx.length)
    }
  }, [shoppingCartQuery.data, messagesQuery.data, loggedOutCartCtx])


  const loggedOutCartTotalQuery = useQuery<ITotalForProducts>(["loggedOutCartTotal", loggedOutCartCtx?.map(entry => entry.quantity)],
    () => fetchLoggedOutCartTotal(loggedOutCartCtx, countryLang), {
      enabled: countryLang != null && (loggedOutCartCtx != null && loggedOutCartCtx.length > 0)
    })


  const handleToCheckout = async () => {
    await router.push(`/${countryLang}/checkout`)
  }


  const handleToCart = async () => {
    await router.push(`/${countryLang}/cart`)
  }

  const handleLogin = () => {
    router.push(getNavPath(router, "account/login?redirectCallback=checkout"))
  }

  const cartElements = (
    <>
      <Main>
        {shoppingCartQuery.data && shoppingCartQuery.data.products && shoppingCartQuery.data.products.map((entry: ICartItem) => (
          <MiniCartProduct
            key={entry.product.productID}
            product={entry.product}
            quantity={entry.quantity}
            type="cart"
            shoppingCart={shoppingCartQuery.data.products}
            showSetupOptions={true}
          />
        ))}

        {/* If the user is logged out we render the loggedOutCart
        * that's persisted in localStorage */}
        {(!isUserLoggedIn && loggedOutCartCtx != null) && loggedOutCartCtx.map((entry: ILoggedOutShoppingList) => (
          <MiniCartProduct
            key={entry.product.productID}
            product={entry.product}
            quantity={entry.quantity}
            type="cart"
            showSetupOptions={true}
          />
        ))}
      </Main>
      <Bottom>
        {messagesQuery.data &&
          <>
            {!isUserLoggedIn && loggedOutCartTotalQuery.data &&
              <div className="cart-total">
                {messagesQuery.data.messages["total"]}: {loggedOutCartTotalQuery.data.cartTotal}
              </div>}

            {router && !router.asPath.includes("checkout") &&
              <>
                {isUserLoggedIn && shoppingCartQuery.data &&
                  <div className="cart-total">
                    {messagesQuery.data.messages["total"]}: {shoppingCartQuery.data.cartTotal}
                  </div>}

                {shoppingCartQuery.data && shippingCostsQuery.data &&
                  <div className="shipping-costs">
                    {messagesQuery.data.messages["shipping-cost"]}: {shippingCostsQuery.data.shippingCost}
                  </div>
                }

                {isUserLoggedIn && <Button variant={"primary"}
                                           width="100%"
                                           onClick={handleToCheckout}>
                  {messagesQuery.data.messages["go-to-checkout"]}
                </Button>}
              </>
            }

            <Button width="100%"
                    variant={isUserLoggedIn ? "secondary" : "primary"}
                    onClick={handleToCart}>
              {messagesQuery.data.messages["go-to-cart"]}
            </Button>

            {!isUserLoggedIn &&
              <Button variant={"secondary"}
                      width="100%"
                      onClick={handleLogin}>
                {messagesQuery.data.messages["login"]} & {messagesQuery.data.messages["checkout"]}
              </Button>}
          </>
        }
      </Bottom>
    </>
  )

  const emptyCart = (
    <EmptyCart>
      <Image src="/icons/empty-cart.svg" width={120} height={200} alt="Empty Cart"/>
      {messagesQuery.data && <span>{messagesQuery.data.messages["cart-is-empty"]}</span>}
    </EmptyCart>
  )

  const content = (
    <CartOffCanvas>
      <Header>
        <Icon variant="medium" color={vars.primaryColor}>shopping-cart-line</Icon>
        {hasItems &&
          <>
            {(shoppingCartQuery.data && shoppingCartQuery.data.products && messagesQuery.data) &&
              <span>{shoppingCartQuery.data.products.length} {messagesQuery.data.messages["products"]}</span>
            }

            {(loggedOutCartCtx != null && messagesQuery.data) &&
              <span>{loggedOutCartCtx.length} {messagesQuery.data.messages["products"]}</span>
            }
          </>
        }

        {(!hasItems && messagesQuery.data) && <span>{messagesQuery.data.messages["cart-is-empty"]}</span>}
      </Header>

      {hasItems && cartElements}
      {!hasItems && emptyCart}
    </CartOffCanvas>
  )


  return (
    <Indicator placement={"end"} content={content} iconVariant={"medium"} icon="shopping-cart-line"
               itemCount={cartItemsCount}/>
  )
}

export default IndicatorMiniCart;
